<template>
  <div>
    <b-form-checkbox
      switch
      v-model="showServiceHints"
      class="gid-checkbox-right d-block mb-1 text-right"
    >
      <small class="text-muted">{{ $t('inputs.services.show-hints') }}</small>
    </b-form-checkbox>
    <service
      v-for="({ elementIndex }, key) in selectedServices"
      :key="key"
      :elementIndex="elementIndex"
      :readonly="readonly"
      :allowCrediting="allowCrediting"
      :services-options="servicesOptions"
      :hide-price-selling="hidePriceSelling"
      :hide-price-purchasing="hidePricePurchasing"
      :hide-price-list="hidePriceListing"
      :edit-services="editServices"
      :edit-price-selling="editPriceSelling"
      :edit-price-purchasing="editPricePurchasing"
      :edit-price-list="editPriceList"
      :show-service-hints="showServiceHints"
      :editInvoicingTarget="editInvoicingTarget"
      :show-totals="showLineTotals"
      :jobId="jobId"
      :productId="productId"
      :job_view_services="job_view_services"
      :context="context"
      :is-not-new="serviceIsNotNew(elementIndex)"
      :allowSelection="allowSelection"
      :selections="jobServices[elementIndex]?.selections || []"
      :service="jobServices[elementIndex]"
    />
    <div v-if="editServices && !readonly">
      <b-button
        v-e2e:addService
        size="sm"
        variant="outline-primary"
        class="mr-2"
        @click="addService()"
      >
        {{ $t('inputs.services.add') }}
      </b-button>
      <b-button
        v-e2e:resetServices
        size="sm"
        variant="outline-secondary"
        @click="resetServices"
      >
        {{ $t('reset') }}
      </b-button>
    </div>
    <custom-services
      :showServiceHints="showServiceHints"
      :readonly="readonly"
      :editable="editServices"
      class="mt-1"
      :jobId="this.jobId"
      :allowSelection="allowSelection"
      v-model="existent_custom_services"
      :showCreate="false"
      :context="
        context === 'admin' || context === 'brand' ? context : 'partner'
      "
      :editInvoicingTarget="editInvoicingTarget"
    />
    <additional-items
      :jobId="this.jobId"
      :hidePriceSelling="hidePriceSelling"
      :hidePricePurchasing="hidePricePurchasing"
      :hidePriceList="hidePriceListing"
      :editable="editServices"
      :editPriceSelling="editPriceSelling"
      :editPricePurchasing="editPricePurchasing"
      :editPriceList="editPriceList"
      :editInvoicingTarget="editInvoicingTarget"
      :context="context"
    />
    <div class="d-flex flex-column justify-content-end">
      <custom-services
        v-if="editServices && context != 'brand'"
        class="mt-3"
        :jobId="this.jobId"
        v-model="custom_services"
        :context="context === 'admin' ? context : 'partner'"
        :editable="editServices"
        :showCreate="true"
        :readonly="readonly"
        :disableCustomServices="shouldDisableCustomServicesCreation"
      />
      <div class="ml-auto" v-if="showPrices">
        <strong>{{ $t('inputs.services.price') }}:</strong>
        <s class="text-muted mr-1" v-if="priceChanged">
          <template v-if="!hidePriceSelling">
            {{ currentPriceSelling | currency }}
          </template>
          <template v-if="!hidePriceSelling && !hidePricePurchasing">
            /
          </template>
          <template v-if="!hidePricePurchasing">
            {{ currentPricePurchasing | currency }}
          </template>
        </s>
        <span v-if="!hidePriceListing" title="List price" v-b-tooltip.hover>
          {{ newPriceListNet | currency }}
        </span>
        <template v-if="!hidePriceSelling && !hidePricePurchasing">/</template>
        <span v-if="!hidePriceSelling" title="Selling price" v-b-tooltip.hover>
          {{ newPriceSellingNet | currency }}
        </span>
        <template v-if="!hidePriceSelling && !hidePricePurchasing">/</template>
        <span
          v-if="!hidePricePurchasing"
          title="Purchasing price"
          v-b-tooltip.hover
        >
          {{ newPricePurchasingNet | currency }}
        </span>
        <div
          v-if="
            pricePurchasingChangeDelta &&
            context != 'admin' &&
            context != 'brand'
          "
          class="ml-auto"
          :class="
            pricePurchasingChangeDelta >= 0 ? 'text-success' : 'text-danger'
          "
        >
          <strong>
            {{ $t('partner-price-difference') }}:
            {{ pricePurchasingChangeDelta >= 0 ? '+' : '' }}
            {{ pricePurchasingChangeDelta | currency }}
          </strong>
        </div>
      </div>
      <template v-if="showSplitCost">
        <div class="ml-auto">
          <strong>{{ $t('inputs.services.labor') }}:</strong>
          <span
            class="mr-1"
            :title="$t('inputs.services.list-price-labor')"
            v-b-tooltip.hover
          >
            {{ listPriceLaborCost | currency }}
          </span>
          <span class="mr-1">/</span>
          <span
            class="mr-1"
            :title="$t('inputs.services.selling-price-labor')"
            v-b-tooltip.hover
          >
            {{ sellingPriceLaborCost | currency }}
          </span>
        </div>
        <div class="ml-auto">
          <strong>{{ $t('inputs.services.material') }}:</strong>
          <span
            class="mr-1"
            :title="$t('inputs.services.list-price-material')"
            v-b-tooltip.hover
          >
            {{ listPriceMaterialCost | currency }}
          </span>
          <span class="mr-1">/</span>
          <span
            class="mr-1"
            :title="$t('inputs.services.selling-price-material')"
            v-b-tooltip.hover
          >
            {{ sellingPriceMaterialCost | currency }}
          </span>
        </div>
      </template>
      <template v-if="showRevenueCost">
        <div class="ml-auto">
          <strong>Revenue:</strong>
          <span class="mr-1">{{ revenue | currency }}</span>
          <strong>Cost:</strong>
          <span class="mr-1">{{ costs | currency }}</span>
        </div>
        <div class="ml-auto">
          <strong>Gross Margin:</strong>
          <span class="mr-1">{{ (revenue - costs) | currency }}</span>
          <p
            v-if="marginCalculation"
            :style="{ color: `${revenue - costs > 0 ? '#fd7e14' : 'red'}` }"
          >
            {{ marginCalculation }}
          </p>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { calculatePriceSplit } from '@gid/api-client';
import {
  GeneralInvoicingTargetEnum,
  InvoicingTargetEnum,
  SupplierTypesEnum,
} from '@gid/models';
import { mapGetters } from 'vuex';

import { moduleName as jobAddtionalItemsModuleName } from '../../store/job-additional-items';
import {
  SERVICE_SELECTED_ADD,
  SERVICE_SELECTED_REFRESH,
} from '../../store/services.module/types';
import AdditionalItems from './AdditionalItems';
import CustomServices from './CustomServices.vue';
import BaseInput from './mixins/BaseInput';
import LimitServiceOptions from './mixins/LimitServiceOptions';
import Service from './Service';

export default {
  mixins: [BaseInput, LimitServiceOptions],
  components: {
    Service,
    AdditionalItems,
    CustomServices,
  },
  props: {
    shouldDisableCustomServicesCreation: {
      type: Boolean,
      default: false,
    },
    allowDuplicateServices: {
      type: Boolean,
      default: false,
    },
    allowCrediting: {
      type: Boolean,
      default: false,
    },
    currentPricePurchasing: Number,
    currentPriceSelling: Number,
    products: Array,
    editServices: {
      type: Boolean,
      default: true,
    },
    allowSelection: {
      type: Boolean,
      default: false,
    },
    editPriceSelling: Boolean,
    editPricePurchasing: Boolean,
    editPriceList: Boolean,
    editInvoicingTarget: Boolean,
    jobId: {
      type: String,
      default: 'new',
    },
    hidePricePurchasing: Boolean,
    hidePriceSelling: Boolean,
    hidePriceListing: {
      type: Boolean,
      default: true,
    },
    productId: {
      type: String,
      default: undefined,
    },
    showLineTotals: Boolean,
    revenue: Number,
    costs: Number,
    job_view_services: { type: Array, default: undefined },
    context: String,
    existent_custom_services: { type: Array },
    custom_services: { type: Array },
    jobServices: { type: Array, default: () => [] },
  },
  data() {
    return {
      showServiceHints: false,
      currentServices: [],
      currentPricePurchasingNet: 0,
      listPriceLaborCost: 0,
      listPriceMaterialCost: 0,
      sellingPriceLaborCost: 0,
      sellingPriceMaterialCost: 0,
    };
  },
  computed: {
    ...mapGetters(['locale']),

    marginCalculation() {
      const margin = this.revenue - this.costs;
      if (margin < 0) {
        return this.$t('negative-margin');
      }
      if (this.revenue * 0.2 > margin) {
        return this.$t('margin-less-twenty');
      }

      return null;
    },
    pricePurchasingChangeDelta() {
      return this.newPricePurchasingNet - this.currentPricePurchasingNet;
    },
    proudctsListingSum() {
      return this.products
        .filter(({ supplier }) => supplier == SupplierTypesEnum.GID)
        .reduce((acc, p) => {
          return acc + (p.price_listing ?? 0) * p.quantity;
        }, 0);
    },
    productsSellingSum() {
      return this.products
        .filter(({ supplier }) => supplier == SupplierTypesEnum.GID)
        .reduce((acc, p) => {
          return acc + (p.price_selling_net ?? 0) * p.quantity;
        }, 0);
    },
    productPurchasingSum() {
      return this.products
        .filter(({ supplier }) => supplier == SupplierTypesEnum.GID)
        .reduce((acc, p) => {
          return acc + (p.price_purchasing_net ?? 0) * p.quantity;
        }, 0);
    },
    newPriceListNet() {
      const additionalItemsPrice = this.$store.getters[
        `${jobAddtionalItemsModuleName}/priceListsSum`
      ](this.jobId);
      const existentCustomServicesPrice =
        this.existent_custom_services?.reduce((acc, cur) => {
          acc += Number(cur.price_lists * cur.quantity);
          return acc;
        }, 0) || 0;
      const currentPrice =
        this.$store.getters.servicesPriceNet({
          jobId: this.jobId,
          targetField: 'price_lists',
          invoicingTarget: [InvoicingTargetEnum.CUSTOMER],
          job_view_services: this.job_view_services,
        }) +
        this.proudctsListingSum +
        additionalItemsPrice +
        existentCustomServicesPrice;
      let finalPrice = 0;
      if (this.custom_services?.length) {
        finalPrice = this.custom_services?.reduce((acc, curr) => {
          acc += Number(curr.price_lists * curr.quantity) || 0;
          return acc;
        }, currentPrice);
      }
      return this.custom_services?.length ? finalPrice : currentPrice;
    },
    newPriceSellingNet() {
      const additionalItemsPrice = this.$store.getters[
        `${jobAddtionalItemsModuleName}/priceSellingSum`
      ](this.jobId);
      const existentCustomServicesPrice =
        this.existent_custom_services?.reduce((acc, cur) => {
          acc += Number(cur.price_selling * cur.quantity);
          return acc;
        }, 0) || 0;
      const currentPrice =
        this.$store.getters.servicesPriceNet({
          jobId: this.jobId,
          targetField: 'price_selling',
          invoicingTarget: [
            InvoicingTargetEnum.BRAND,
            InvoicingTargetEnum.BRAND_1,
            InvoicingTargetEnum.BRAND_2,
          ],
          job_view_services: this.job_view_services,
        }) +
        additionalItemsPrice +
        existentCustomServicesPrice +
        this.productsSellingSum;
      let finalPrice = 0;
      if (this.custom_services?.length) {
        finalPrice = this.custom_services?.reduce((acc, curr) => {
          acc += Number(curr.price_selling * curr.quantity) || 0;
          return acc;
        }, currentPrice);
      }
      return this.custom_services?.length ? finalPrice : currentPrice;
    },
    newPricePurchasingNet() {
      const additionalItemsPrice = this.$store.getters[
        `${jobAddtionalItemsModuleName}/pricePurchasingSum`
      ](this.jobId);
      const existentCustomServicesPrice =
        this.existent_custom_services?.reduce((acc, cur) => {
          acc += Number(cur.price_purchasing * cur.quantity);
          return acc;
        }, 0) || 0;
      let currentPrice =
        this.$store.getters.servicesPriceNet({
          jobId: this.jobId,
          targetField: 'price_purchasing',
          invoicingTarget: Object.values(InvoicingTargetEnum),
          job_view_services: this.job_view_services,
        }) +
        additionalItemsPrice +
        existentCustomServicesPrice +
        this.productPurchasingSum;
      let finalPrice = 0;
      if (this.custom_services?.length) {
        finalPrice = this.custom_services?.reduce((acc, curr) => {
          acc += Number(curr.price_purchasing * curr.quantity) || 0;
          return acc;
        }, currentPrice);
      }
      return this.custom_services?.length ? finalPrice : currentPrice;
    },
    serviceDefinitions() {
      return this.$store.getters.serviceDefinitions(this.jobId);
    },
    allSelectedServices() {
      return this.$store.getters.allSelectedServices(this.jobId);
    },
    selectedServices() {
      if (!this.serviceDefinitions) return [];

      const localSelectedService = {};
      const selectedServicesList = this.$store.getters.selectedServices({
        jobId: this.jobId,
        productId: this.productId,
      });
      selectedServicesList.forEach(({ service }) => {
        const serviceDef = this.serviceDefinitions?.[service];
        if (serviceDef) localSelectedService[service] = serviceDef;
        if (!serviceDef) {
          console.error('missing serviceDef', service);
        }
      });

      let arrangedServices = [];

      selectedServicesList.forEach((js) => {
        if (!js.service) return;
        const key = js.service;
        const value = this.serviceDefinitions?.[key];
        let newArrangedService = { ...value, ...{ parentService: undefined } };
        arrangedServices.push(newArrangedService);
      });
      selectedServicesList.forEach((js) => {
        if (!js.service) return;
        const key = js.service;
        const value = this.serviceDefinitions?.[key];
        if (!value) {
          console.warn(`Service definition not found for service: ${key}`);
          return; // Skip this iteration and continue with the next item
        }

        const { only_in_combination_with } = this.serviceDefinitions[key];
        let newArrangedService = { ...value, ...{ parentService: undefined } };
        if (only_in_combination_with.length > 0) {
          const parentIndex = arrangedServices.findIndex(
            (as) =>
              as.parentService === undefined &&
              as.sfid === newArrangedService.sfid,
          );
          if (
            arrangedServices[parentIndex] &&
            !arrangedServices[parentIndex]?.linked_services
          ) {
            arrangedServices[parentIndex].linked_services = {};
          }
          only_in_combination_with.forEach(({ service }) => {
            const serviceDef = this.serviceDefinitions?.[service];
            if (!serviceDef) {
              console.warn(
                `Service definition not found for service: ${service}`,
              );
              return; // Skip this iteration and continue with the next item
            }
            // find an element of arrangedServices array with parentService undefined and
            // arrangedServices[el].sfid === service
            // and update its parentService with the current newArrangedService.sfid:
            let foundIndex = -1;
            try {
              foundIndex = arrangedServices.findIndex(
                (as) =>
                  as.parentService === undefined && as.sfid === serviceDef.sfid,
              );
            } catch (error) {
              console.error(
                'error finding Index',
                arrangedServices,
                serviceDef,
                error,
              );
            }
            if (foundIndex !== -1) {
              arrangedServices[foundIndex] = {
                ...serviceDef,
                ...{ parentService: newArrangedService.sfid },
              };
            }
          });
        }
      });

      const parents = arrangedServices.filter(
        (el) => el.parentService === undefined,
      );
      const children = arrangedServices.filter(
        (el) => el.parentService !== undefined,
      );

      const parentMap = {};
      children.forEach((child) => {
        if (!parentMap[child.parentService]) {
          parentMap[child.parentService] = [];
        }
        parentMap[child.parentService].push(child);
      });

      function serviceCompare(a, b) {
        // sort_index: defined comes before undefined
        if (a.sort_index === undefined && b.sort_index !== undefined) return 1;
        if (a.sort_index !== undefined && b.sort_index === undefined) return -1;

        // If both sort_index are defined, sort ascending
        if (a.sort_index !== undefined && b.sort_index !== undefined) {
          if (a.sort_index !== b.sort_index) {
            return a.sort_index - b.sort_index;
          }
        }
        return a.name.localeCompare(b.name);
      }
      parents.sort(serviceCompare);

      // Sort children for each parent
      Object.values(parentMap).forEach((childList) => {
        childList.sort(serviceCompare);
      });

      // Build the sorted array with parents followed by their children
      const sortedArray = [];
      parents.forEach((parent) => {
        sortedArray.push(parent);
        const childList = parentMap[parent.sfid];
        if (childList) {
          sortedArray.push(...childList);
        }
      });

      const response = [];
      const existingIndices = new Set();
      for (let i = 0; i < selectedServicesList?.length; i++) {
        const service = sortedArray[i];
        let foundIndex = i;

        for (let index = 0; index < selectedServicesList.length; index++) {
          if (
            selectedServicesList[index].service === service?.sfid &&
            !existingIndices.has(index)
          ) {
            foundIndex = index; // if service is set it should always setup
            if (
              selectedServicesList[index]?.requiredBy?.counter &&
              Object.keys(
                selectedServicesList[index]?.requiredBy?.counter,
              ).includes(service?.parentService)
            ) {
              foundIndex = index;
              break;
            }
          }
        }
        existingIndices.add(foundIndex);
        response.push({ elementIndex: foundIndex });
      }
      return response;
    },
    servicesOptions() {
      return this.limitedServiceOptionsMixin(
        this.jobId,
        this.serviceDefinitions,
        this.allowDuplicateServices,
      );
    },
    priceChanged() {
      return (
        this.currentPriceSelling &&
        this.currentPriceSelling.toFixed(2) !=
          this.newPriceSellingNet.toFixed(2)
      );
    },
    showPrices() {
      return (
        !this.hidePriceSelling ||
        !this.hidePriceSelling ||
        !this.hidePricePurchasing
      );
    },
    showRevenueCost() {
      return this.revenue !== undefined || this.costs !== undefined;
    },
    showSplitCost() {
      return (
        this.listPriceMaterialCost !== 0 ||
        this.listPriceLaborCost !== 0 ||
        this.sellingPriceMaterialCost !== 0 ||
        this.sellingPriceLaborCost !== 0
      );
    },
    serviceIsNotNew() {
      return (index) => {
        return Boolean(
          this.currentServices.find(
            (service) => service.elementIndex === index && service.service,
          ),
        );
      };
    },
    selectedProducts() {
      return this.$store.getters
        .selectedProducts(this.jobId)
        .filter(
          ({ group_id, invoicing_target, supplier }) =>
            (!group_id || group_id == this._uid) &&
            invoicing_target !== GeneralInvoicingTargetEnum.GID &&
            supplier === SupplierTypesEnum.GID,
        )
        .map((product, index) => ({
          ...product,
          elementIndex: index,
          service_type: '090.Materialkosten',
        }));
    },
  },
  created() {
    this.currentServices = JSON.parse(JSON.stringify(this.selectedServices));
    if (!this.hidePricePurchasing) {
      this.currentPricePurchasingNet = this.newPricePurchasingNet;
    }
  },
  methods: {
    getActiveService(elementIndex) {
      return this.$store.getters.activeService({
        elementIndex,
        jobId: this.jobId,
        productId: this.productId,
      });
    },
    calculatePriceSplitData() {
      // Map selected services to their definitions and quantities
      const services = this.allSelectedServices
        .map(({ service, elementIndex }) => {
          const serviceDef = this.serviceDefinitions[service];
          const activeService = this.getActiveService(elementIndex);
          return serviceDef ? { ...serviceDef, ...activeService } : null;
        })
        .filter(Boolean); // Remove undefined/null entries

      if (
        !services.length &&
        !this.existent_custom_services &&
        !this.selectedProducts.length
      ) {
        console.warn('No valid services found after mapping.');
        return;
      }

      const allServiceEntities = [
        ...services,
        ...this.existent_custom_services,
        ...this.selectedProducts,
      ];

      const priceSplitPayload = this.createPriceSplitPayload(
        allServiceEntities,
        this.serviceDefinitions,
      );

      this.listPriceLaborCost = 0;
      this.listPriceMaterialCost = 0;
      this.sellingPriceLaborCost = 0;
      this.sellingPriceMaterialCost = 0;

      // Calculate labor and material costs
      priceSplitPayload.forEach(
        ({
          serviceDisplayListPriceSum,
          serviceDisplaySellingPriceSum,
          parentServiceType,
          totalPrice,
          childServices,
        }) => {
          const priceSplit = calculatePriceSplit(
            parentServiceType,
            totalPrice,
            childServices,
          );

          this.listPriceLaborCost +=
            priceSplit.labor * serviceDisplayListPriceSum;
          this.listPriceMaterialCost +=
            priceSplit.products * serviceDisplayListPriceSum;
          this.sellingPriceLaborCost +=
            priceSplit.labor * serviceDisplaySellingPriceSum;
          this.sellingPriceMaterialCost +=
            priceSplit.products * serviceDisplaySellingPriceSum;
        },
      );

      // Round final values
      this.listPriceLaborCost = this.roundToTwoDecimals(
        this.listPriceLaborCost,
      );
      this.listPriceMaterialCost = this.roundToTwoDecimals(
        this.listPriceMaterialCost,
      );
      this.sellingPriceLaborCost = this.roundToTwoDecimals(
        this.sellingPriceLaborCost,
      );
      this.sellingPriceMaterialCost = this.roundToTwoDecimals(
        this.sellingPriceMaterialCost,
      );
    },
    roundToTwoDecimals(value) {
      return Math.round(value * 100) / 100;
    },
    createPriceSplitPayload(services, serviceDefinitions) {
      return services.map((service) => {
        const {
          price_lists = 0,
          price_listing = 0,
          price_selling = 0,
          price_purchasing = 0,
          quantity = 1,
          only_in_combination_with = [],
          service_type,
        } = service;

        const listPrice = price_lists || price_listing;
        const serviceDisplayListPriceSum = listPrice * quantity || 0;

        const sellingPrice = price_selling || 0;
        const serviceDisplaySellingPriceSum = sellingPrice * quantity || 0;

        // Process linked services
        const linkedServices = only_in_combination_with.length
          ? only_in_combination_with
              .map(({ service, quantity }) => {
                const linkedService = serviceDefinitions[service];
                if (!linkedService) {
                  console.warn(`Linked service with ID ${service} not found`);
                  return null;
                }
                return {
                  purchasePrice: linkedService.price_purchasing,
                  sellingPrice: linkedService.price_selling,
                  quantity,
                  serviceType: linkedService.service_type,
                };
              })
              .filter(Boolean) // Remove undefined/null entries
          : [
              {
                purchasePrice: price_purchasing,
                sellingPrice: price_selling,
                quantity: 1,
                serviceType: service_type,
              },
            ];

        return {
          parentServiceType: service_type,
          totalPrice: 1,
          childServices: linkedServices,
          serviceDisplayListPriceSum, // Sum for price_lists
          serviceDisplaySellingPriceSum, // Sum for price_selling
        };
      });
    },
    resetServices() {
      this.$store.dispatch(SERVICE_SELECTED_REFRESH, { jobId: this.jobId });
    },
    addService(serviceSfid) {
      const selectedServicesList = this.$store.getters.selectedServices({
        jobId: this.jobId,
        productId: this.productId,
      });

      // Prevent from adding multiple empty rows
      if (
        selectedServicesList.length === 0 ||
        selectedServicesList[selectedServicesList.length - 1].service
      ) {
        if (typeof serviceSfid !== 'string') {
          serviceSfid = undefined;
        }
        this.$store.dispatch(SERVICE_SELECTED_ADD, {
          serviceSfid: serviceSfid,
          jobId: this.jobId,
        });
      }
    },
  },
  mounted() {
    if (this.serviceDefinitions) this.calculatePriceSplitData();
  },
  watch: {
    serviceDefinitions: {
      handler(newValue) {
        if (newValue) {
          this.calculatePriceSplitData();
        }
      },
      immediate: true,
    },
  },
};
</script>

<style lang="scss">
.gid-service-hints {
  gap: 10px;
  display: flex;
  p {
    margin: 0;
  }
}
</style>
